<template>
  <app-page class="rolls-page" fill lean>
    <!-- Kopf und Werkzeugleiste wie Live-Tracking (Kundenwunsch 27.10.2026: Verlauf rechts nicht direkt an den Knöpfen):
         ohne Filter und Gruppierung steht „Aktualisieren“ rechts in der Werkzeugleiste, im Kopf der Rollennachweis;
         die Werkzeugleiste trennt Kopf und Inhalt mit ihrer Linie, der Verlauf beginnt darunter -->
    <template #actions>
      <app-button v-if="!handy" icon="download" :disabled="!canExport" @click="exportReport">Rollennachweis</app-button>
      <template v-else>
        <app-icon-button icon="refresh" label="Aktualisieren" @click="load(true)" />
        <app-icon-button icon="download" label="Rollennachweis (Excel)" :disabled="!canExport" @click="exportReport" />
      </template>
    </template>
    <template #toolbar>
      <app-search v-model="searchText" placeholder="Rolle oder Drucker suchen" />
      <span class="rolls__meta">
        Letzte {{ windowDays }} Tage<template v-if="result && kpis.devicesTracked"> · Kapazität {{ capacityText }}</template><template v-if="stand"> · Stand {{ stand }}</template>
      </span>
    </template>
    <template v-if="!handy" #toolbar-end>
      <app-button icon="refresh" :loading="loading" @click="load(true)">Aktualisieren</app-button>
    </template>

    <div class="rolls-scroll">
      <div class="rolls app-page-pad" :class="{ 'rolls--phone': handy }">

        <app-panel v-if="error && !result" nested icon="scroll" title="Rollen" state="error" @retry="load(true)" />
        <app-panel v-else-if="!result" nested icon="scroll" title="Rollen" skeleton="rows" :skeleton-rows="4" />

        <template v-else>
          <!-- Kennzahlen nur, wenn ein Gerät mit Rollenverfolgung verkauft hat -->
          <section v-if="kpis.devicesTracked" class="rolls__kpis" aria-label="Kennzahlen">
            <app-kpi-tile icon="tablet" title="Geräte mit Rollenverfolgung" :graphic-height="6">
              <template v-slot:value>{{ kpis.devicesOk }}<span class="rolls__of"> von {{ kpis.devicesTracked }} okay</span></template>
              <template v-slot:graphic>
                <app-share-bar :parts="deviceParts" :total="kpis.devicesTracked" :gap="2" />
              </template>
              <template v-slot:foot>
                <span>{{ deviceFoot }}</span>
              </template>
            </app-kpi-tile>
            <app-kpi-tile icon="scroll" title="Rollen im Einsatz" :graphic-height="6">
              <template v-slot:value>{{ kpis.activeRolls }}</template>
              <template v-slot:graphic>
                <app-share-bar :parts="restParts" :total="restTotal" />
              </template>
              <template v-slot:foot>
                <span>{{ restFoot }}</span>
                <span v-if="!handy">{{ kpis.finishedRolls }} verbraucht</span>
              </template>
            </app-kpi-tile>
            <app-kpi-tile icon="alertTriangle" title="Unerklärte Lücken" :graphic-height="6">
              <template v-slot:value>
                <span :class="{ 'rolls__bad': kpis.unexplainedGaps }">{{ kpis.unexplainedGaps }}</span>
              </template>
              <template v-slot:foot>
                <span>{{ kpis.unexplainedSections }} {{ kpis.unexplainedSections === 1 ? 'Abschnitt' : 'Abschnitte' }}</span>
                <span v-if="!handy">{{ gapFoot }}</span>
              </template>
            </app-kpi-tile>
            <app-kpi-tile icon="ticket" title="Ohne Rollennummer" :graphic-height="6">
              <template v-slot:value>
                <span :class="{ 'rolls__bad': kpis.withoutRoll }">{{ kpis.withoutRoll }}</span><span class="rolls__of"> von {{ kpis.gebeSales }}</span>
              </template>
              <template v-slot:graphic>
                <app-share-bar :parts="missingParts" :total="Math.max(1, kpis.gebeSales)" />
              </template>
              <template v-slot:foot>
                <span>Verkäufe mit GeBE-Drucker</span>
              </template>
            </app-kpi-tile>
          </section>
          <p v-else class="rolls__none">
            <app-icon name="info" :size="16" />
            <span>In den letzten {{ windowDays }} Tagen hat kein Gerät mit Rollenverfolgung verkauft. Die Rollenverfolgung
              liest beim Druck mit einem GeBE-Drucker die Rollennummer der MVV-Thermorolle; andere Drucker melden keine
              Rollennummern.</span>
          </p>

          <!-- Geräte -->
          <app-panel nested icon="tablet" title="Geräte" :subtitle="deviceSubtitle" flush>
            <ul v-if="trackedDevices.length" class="rdev" aria-label="Geräte mit Rollenverfolgung">
              <li v-for="d in trackedDevices" :key="d.printer_id">
                <button type="button" class="rdev__row" :disabled="!d.currentRoll" @click="select(d.currentRoll)">
                  <span class="rdev__dot" :class="'rdev__dot--' + d.state.level" aria-hidden="true"></span>
                  <span class="rdev__name">
                    <strong>Drucker {{ d.printer_id }}</strong>
                    <span v-if="d.comment">{{ d.comment }}</span>
                  </span>
                  <span class="rdev__state">
                    <span :class="'rdev__text--' + d.state.level">{{ d.state.text }}</span>
                    <span class="rdev__detail">{{ d.state.detail }}</span>
                  </span>
                  <span class="rdev__roll">
                    <template v-if="d.currentRoll">
                      <span>Rolle {{ d.currentRoll.id }}</span>
                      <span class="rdev__detail">{{ restPercent(d.currentRoll) }} übrig</span>
                    </template>
                    <span v-else class="rdev__detail">keine Rolle erkannt</span>
                  </span>
                  <span class="rdev__time" :title="exact(d.last_sale)">{{ relative(d.last_sale) }}</span>
                </button>
              </li>
            </ul>
            <template v-if="untrackedDevices.length">
              <button
                v-if="trackedDevices.length"
                type="button"
                class="rdev__toggle"
                :aria-expanded="showUntracked ? 'true' : 'false'"
                @click="showUntracked = !showUntracked"
              >
                <app-icon :name="showUntracked ? 'chevD' : 'chevR'" :size="14" />
                Geräte ohne Rollenverfolgung ({{ untrackedDevices.length }})
              </button>
              <ul v-if="showUntracked || !trackedDevices.length" class="rdev rdev--plain" aria-label="Geräte ohne Rollenverfolgung">
                <li v-for="d in untrackedDevices" :key="d.printer_id" class="rdev__row rdev__row--static">
                  <span class="rdev__dot rdev__dot--none" aria-hidden="true"></span>
                  <span class="rdev__name">
                    <strong>Drucker {{ d.printer_id }}</strong>
                    <span v-if="d.comment">{{ d.comment }}</span>
                  </span>
                  <span class="rdev__state">
                    <span>{{ d.state.text }}</span>
                    <span class="rdev__detail">{{ d.state.detail }}</span>
                  </span>
                  <span class="rdev__roll"></span>
                  <span class="rdev__time" :title="exact(d.last_sale)">{{ relative(d.last_sale) }}</span>
                </li>
              </ul>
            </template>
            <p v-if="!result.devices.length" class="rolls__empty">Keine Verkäufe in den letzten {{ windowDays }} Tagen.</p>
            <p v-else-if="query && !trackedDevices.length && !untrackedDevices.length" class="rolls__empty">Kein Gerät zur Suche.</p>
          </app-panel>

          <!-- Rollen im Einsatz -->
          <app-panel v-if="kpis.devicesTracked" nested icon="scroll" title="Rollen im Einsatz" :subtitle="String(activeRolls.length)">
            <div v-if="activeRolls.length" class="rolls__cards">
              <roll-card
                v-for="roll in activeRolls"
                :key="roll.id"
                :roll="roll"
                :printer-names="printerNames"
                :selected="selectedId === roll.id"
                :now="now"
                @select="select"
              />
            </div>
            <p v-else class="rolls__empty">{{ query ? 'Keine Rolle zur Suche.' : 'Keine Rolle mit erkannter Rollennummer im Einsatz.' }}</p>
          </app-panel>

          <!-- Verbrauchte Rollen (letzte 30 Tage) -->
          <app-panel
            v-if="finishedRolls.length"
            nested
            icon="checkCheck"
            title="Verbrauchte Rollen"
            :subtitle="'letzte ' + windowDays + ' Tage'"
            flush
          >
            <template #actions>
              <button type="button" class="app-link" :aria-expanded="showFinished ? 'true' : 'false'" @click="showFinished = !showFinished">
                {{ showFinished ? 'Ausblenden' : 'Anzeigen (' + finishedRolls.length + ')' }}
              </button>
            </template>
            <ul v-if="showFinished" class="rdone" aria-label="Verbrauchte Rollen">
              <li v-for="roll in finishedRolls" :key="roll.id">
                <button type="button" class="rdone__row" :class="{ 'rdone__row--selected': selectedId === roll.id }" @click="select(roll)">
                  <roll-graphic :share="0" level="done" :size="34" />
                  <span class="rdone__main">
                    <strong>Rolle {{ roll.id }}</strong>
                    <span class="rdev__detail">Drucker {{ roll.printers.join(', ') }} · Abschnitt {{ rangeOf(roll.min, roll.max) }}</span>
                  </span>
                  <span class="rdone__state">
                    <span>{{ roll.full ? 'vollständig verbraucht' : 'beendet bei ' + roll.max }}</span>
                    <span class="rdev__detail" :class="{ 'rolls__bad': roll.unexplained.length }">{{ gapText(roll) }}</span>
                  </span>
                  <span class="rdev__time" :title="exact(roll.lastTime)">{{ relative(roll.lastTime) }}</span>
                </button>
              </li>
            </ul>
          </app-panel>
        </template>
      </div>
    </div>

    <template #detail>
      <app-detail-pane
        :open="!!selectedRoll"
        icon="scroll"
        :title="selectedRoll ? 'Rolle ' + selectedRoll.id : ''"
        :subtitle="selectedRoll ? detailSubtitle : ''"
        close-label="Verlauf schließen"
        medium
        @close="selectedId = null"
      >
        <roll-detail v-if="selectedRoll" :roll="selectedRoll" :capacity="result.capacity" :printer-names="printerNames" />
      </app-detail-pane>
    </template>
  </app-page>
</template>

<script>
// Verwaltung → Rollen: Rollenverfolgung der Thermorollen (Kundenwunsch 27.10.2026, wörtlich u. a. „ob auf allen Geräten
// die Rollenverfolgung funktioniert … welche Rolle, wie viel aufgebraucht worden ist … ob es irgendwelche Lücken von den
// Rollenabschnitten gab … die, die schon vollständig aufgebraucht worden sind, können irgendwie sichtbar sein“; bei allen
// Kunden, Inhalt nur aus den letzten 30 Tagen). Daten: ticketsale?option=rollTracking, Auswertung in
// src/components/rolls/rollTracking.js. Aufbau: Kennzahlen (nur mit Rollenverfolgung), Geräte (mit Status, aktueller
// Rolle und letztem Verkauf; Geräte ohne Rollenverfolgung eingeklappt), Rollen im Einsatz als Karten mit gezeichneter
// Rolle und Abschnittsband, verbrauchte Rollen eingeklappt; Verlauf einer Rolle im Detailbereich. Aktualisiert sich jede
// Minute, solange die Seite sichtbar ist. Excel „Rollennachweis“ (rollReport.js).
import axios from 'axios';
import moment from 'moment';
import AppShareBar from '@/components/ui/AppShareBar';
import RollCard from '@/components/rolls/RollCard';
import RollDetail from '@/components/rolls/RollDetail';
import RollGraphic from '@/components/rolls/RollGraphic';
import { analyzeRolls, rangeText } from '@/components/rolls/rollTracking';
import { exportRollReport } from '@/components/rolls/rollReport';
import { relativeTime, exactTime } from '@/utils/relativeTime';

const REFRESH_MS = 60000;

export default {
  name: 'RollTracking',
  components: { AppShareBar, RollCard, RollDetail, RollGraphic },
  data() {
    return {
      result: null,
      loading: false,
      error: false,
      loadedAt: null,
      now: new Date(),
      selectedId: null,
      showFinished: false,
      showUntracked: false,
      searchText: ''
    };
  },
  computed: {
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    windowDays() {
      return this.result ? this.result.windowDays : 30;
    },
    kpis() {
      return this.result ? this.result.kpis : {};
    },
    stand() {
      return this.loadedAt ? moment(this.loadedAt).format('HH:mm') + ' Uhr' : '';
    },
    capacityText() {
      const c = this.result.capacity;
      return c.value + ' Abschnitte (' + (c.learned ? 'gelernt' : 'geschätzt') + ')';
    },
    printerNames() {
      const names = {};
      (this.result ? this.result.devices : []).forEach((d) => {
        if (d.comment) {
          names[d.printer_id] = d.comment;
        }
      });
      return names;
    },
    // Suche nach Rollennummer, Drucker-ID oder Bemerkung (Kennzahlen bleiben für alle Geräte und Rollen)
    query() {
      return String(this.searchText || '').trim().toLowerCase();
    },
    shownDevices() {
      if (!this.result) {
        return [];
      }
      const q = this.query;
      return q ? this.result.devices.filter((d) => this.deviceMatches(d, q)) : this.result.devices;
    },
    trackedDevices() {
      return this.shownDevices.filter((d) => d.state.level !== 'none');
    },
    untrackedDevices() {
      return this.shownDevices.filter((d) => d.state.level === 'none');
    },
    activeRolls() {
      return this.result ? this.result.active.filter((r) => this.rollMatches(r, this.query)) : [];
    },
    finishedRolls() {
      return this.result ? this.result.finished.filter((r) => this.rollMatches(r, this.query)) : [];
    },
    deviceSubtitle() {
      return (this.result ? this.result.devices.length : 0) + ' mit Verkäufen';
    },
    deviceParts() {
      return [
        { value: this.kpis.devicesOk, color: 'st-ok' },
        { value: this.kpis.devicesWarn, color: 'st-warn' },
        { value: this.kpis.devicesError, color: 'st-late' }
      ];
    },
    deviceFoot() {
      const k = this.kpis;
      const parts = [];
      if (k.devicesError) {
        parts.push(k.devicesError + ' gestört');
      }
      if (k.devicesWarn) {
        parts.push(k.devicesWarn + ' mit Warnung');
      }
      return parts.length ? parts.join(' · ') : 'alle melden Rollennummern';
    },
    restTotal() {
      return this.result ? this.result.active.reduce((n, r) => n + r.capacity, 0) || 1 : 1;
    },
    restParts() {
      const rest = this.result ? this.result.active.reduce((n, r) => n + r.rest, 0) : 0;
      return [{ value: rest, color: 'd-std' }];
    },
    restFoot() {
      const low = this.result ? this.result.active.filter((r) => r.restShare < 0.08).length : 0;
      return low ? low + ' fast leer' : 'Rest gesamt';
    },
    // erklärte und unkritische Lücken (einzelner Abschnitt) zählen nicht als unerklärt
    gapFoot() {
      const parts = [this.kpis.explainedGaps + ' erklärt'];
      if (this.kpis.minorGaps) {
        parts.push(this.kpis.minorGaps + ' unkritisch');
      }
      return parts.join(' · ');
    },
    missingParts() {
      return [{ value: this.kpis.withoutRoll, color: 'st-late' }];
    },
    canExport() {
      return !!(this.result && (this.result.active.length || this.result.finished.length));
    },
    selectedRoll() {
      if (!this.result || !this.selectedId) {
        return null;
      }
      return this.result.active.concat(this.result.finished).find((r) => r.id === this.selectedId) || null;
    },
    detailSubtitle() {
      const r = this.selectedRoll;
      return r.status === 'active' ? 'Drucker ' + r.activeOn.join(', ') : (r.full ? 'verbraucht' : 'beendet');
    }
  },
  mounted() {
    this.load(false);
    this._timer = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
        this.load(false);
      }
    }, REFRESH_MS);
  },
  beforeDestroy() {
    clearInterval(this._timer);
  },
  methods: {
    async load(manual) {
      if (this.loading) {
        return;
      }
      this.loading = true;
      try {
        const response = await axios.get(`${this.$store.state.baseUrl}ticketsale?option=rollTracking`);
        this.now = new Date();
        this.result = analyzeRolls(response.data || {}, this.now);
        this.loadedAt = this.now;
        this.error = false;
        // gewählte Rolle bleibt offen, solange sie noch angezeigt wird
        if (this.selectedId && !this.selectedRoll) {
          this.selectedId = null;
        }
      } catch (e) {
        // automatische Aktualisierung: alter Stand bleibt stehen; nur ohne Daten oder per Knopf den Fehler zeigen
        if (manual || !this.result) {
          this.error = true;
        }
      } finally {
        this.loading = false;
      }
    },
    deviceMatches(d, q) {
      return String(d.printer_id).indexOf(q) !== -1
        || String(d.comment || '').toLowerCase().indexOf(q) !== -1
        || !!(d.currentRoll && d.currentRoll.id.indexOf(q) !== -1);
    },
    rollMatches(roll, q) {
      return !q || roll.id.indexOf(q) !== -1
        || roll.printers.some((p) => String(p).indexOf(q) !== -1 || String(this.printerNames[p] || '').toLowerCase().indexOf(q) !== -1);
    },
    select(roll) {
      if (roll) {
        this.selectedId = this.selectedId === roll.id ? null : roll.id;
      }
    },
    async exportReport() {
      if (!this.canExport) {
        return;
      }
      const customer = this.$store.state.customer;
      await exportRollReport(this.result, {
        customerName: (customer && customer.cust_printname) || this.$store.state.customerName || '',
        printerNames: this.printerNames,
        now: new Date()
      });
    },
    relative(value) {
      return relativeTime(value, this.now) || '–';
    },
    exact(value) {
      return exactTime(value);
    },
    rangeOf(a, b) {
      return rangeText(a, b);
    },
    restPercent(roll) {
      return Math.round(roll.restShare * 100) + ' %';
    },
    gapText(roll) {
      const open = roll.unexplained.length;
      if (open) {
        return open + (open === 1 ? ' unerklärte Lücke' : ' unerklärte Lücken');
      }
      return roll.gaps.length ? 'keine kritischen Lücken' : 'keine Lücken';
    }
  }
};
</script>

<style scoped>
/* Inhalt scrollt in sich, der Verlauf rechts bleibt stehen (fill); auf dem Handy natürliche Höhe */
.rolls-scroll {
  flex: 999 1 560px;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
}
.rolls {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 16px;
  padding-bottom: 20px;
  container-type: inline-size;
}
.rolls--phone {
  gap: 12px;
}
.rolls__meta {
  min-width: 0;
  margin-left: 4px;
  font-size: 13px;
  line-height: 18px;
  color: var(--ink-3);
}
/* vier Kacheln nebeneinander, wenn die Inhaltsspalte breit genug ist (auch bei offenem Verlauf), sonst 2 × 2 */
.rolls__kpis {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
@container (min-width: 980px) {
  .rolls__kpis {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
.rolls--phone .rolls__kpis {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.rolls__of {
  font-size: 13px;
  font-weight: 400;
  color: var(--ink-3);
}
.rolls__bad {
  color: var(--red);
}
.rolls__none {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 12px 14px;
  border-radius: var(--r-ctl);
  background: var(--band);
  color: var(--ink-2);
  font-size: 13px;
  line-height: 20px;
}
.rolls__none .app-icon {
  flex: none;
  margin-top: 2px;
  color: var(--ink-3);
}
.rolls__empty {
  margin: 0;
  padding: 12px 14px;
  color: var(--ink-3);
  font-size: 13px;
}
.rolls__cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(320px, 100%), 1fr));
  gap: 12px;
}

/* Geräteliste */
.rdev {
  margin: 0;
  padding: 0;
  list-style: none;
}
.rdev__row {
  width: 100%;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 10px minmax(150px, 1.2fr) minmax(180px, 1.6fr) minmax(110px, 1fr) minmax(90px, auto);
  align-items: center;
  gap: 4px 14px;
  min-height: 52px;
  margin: 0;
  padding: 7px 14px;
  border: 0;
  border-bottom: 1px solid var(--line-2);
  background: transparent;
  color: var(--ink);
  font-family: var(--font);
  font-size: 13px;
  line-height: 18px;
  text-align: left;
  cursor: pointer;
}
.rdev > li:last-child > .rdev__row,
.rdev > li.rdev__row:last-child {
  border-bottom: 0;
}
.rdev__row:disabled,
.rdev__row--static {
  cursor: default;
  color: var(--ink);
}
:where(html:not(.dichte-touch)) .rdev__row:not(:disabled):not(.rdev__row--static):hover {
  background: var(--band);
}
.rdev__row:focus {
  outline: none;
}
.rdev__row:focus-visible {
  box-shadow: inset 0 0 0 2px var(--link);
}
.rdev__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
.rdev__dot--ok {
  background: var(--st-ok);
}
.rdev__dot--warn {
  background: var(--st-warn);
}
.rdev__dot--error {
  background: var(--st-late);
}
.rdev__dot--none {
  background: var(--st-none);
}
.rdev__name,
.rdev__state,
.rdev__roll {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.rdev__name > span,
.rdev__detail {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--ink-3);
  font-size: 12px;
  line-height: 17px;
}
.rdev__text--error {
  color: var(--red);
  font-weight: 600;
}
.rdev__text--warn {
  color: var(--amber);
  font-weight: 600;
}
.rdev__roll {
  font-variant-numeric: tabular-nums;
}
.rdev__time {
  color: var(--ink-3);
  font-size: 12px;
  text-align: right;
  white-space: nowrap;
}
.rdev__toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  margin: 0;
  padding: 10px 14px;
  border: 0;
  border-top: 1px solid var(--line-2);
  background: transparent;
  color: var(--ink-2);
  font-family: var(--font);
  font-size: 13px;
  text-align: left;
  cursor: pointer;
}
.rdev__toggle:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px var(--link);
}
.rdev--plain {
  border-top: 1px solid var(--line-2);
}
.rdev--plain .rdev__row {
  min-height: 44px;
}
/* Handy: Name und Zustand untereinander, Zeit rechts */
.rolls--phone .rdev__row {
  grid-template-columns: 10px minmax(0, 1fr) auto;
  grid-template-areas: "dot name time" ". state state" ". roll roll";
  padding: 10px 12px;
}
.rolls--phone .rdev__dot {
  grid-area: dot;
}
.rolls--phone .rdev__name {
  grid-area: name;
}
.rolls--phone .rdev__state {
  grid-area: state;
}
.rolls--phone .rdev__roll {
  grid-area: roll;
}
.rolls--phone .rdev__roll:empty {
  display: none;
}
.rolls--phone .rdev__time {
  grid-area: time;
}

/* verbrauchte Rollen */
.rdone {
  margin: 0;
  padding: 0;
  list-style: none;
}
.rdone__row {
  width: 100%;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 34px minmax(0, 1.4fr) minmax(0, 1fr) auto;
  align-items: center;
  gap: 4px 14px;
  min-height: 52px;
  margin: 0;
  padding: 7px 14px;
  border: 0;
  border-bottom: 1px solid var(--line-2);
  background: transparent;
  color: var(--ink);
  font-family: var(--font);
  font-size: 13px;
  line-height: 18px;
  text-align: left;
  cursor: pointer;
}
.rdone > li:last-child .rdone__row {
  border-bottom: 0;
}
:where(html:not(.dichte-touch)) .rdone__row:hover {
  background: var(--band);
}
.rdone__row--selected {
  background: var(--tint);
}
.rdone__row:focus {
  outline: none;
}
.rdone__row:focus-visible {
  box-shadow: inset 0 0 0 2px var(--link);
}
.rdone__main,
.rdone__state {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.rolls--phone .rdone__row {
  grid-template-columns: 34px minmax(0, 1fr) auto;
  grid-template-areas: "g main time" "g state state";
  padding: 10px 12px;
}
.rolls--phone .rdone__row > :first-child {
  grid-area: g;
}
.rolls--phone .rdone__main {
  grid-area: main;
}
.rolls--phone .rdone__state {
  grid-area: state;
}
.rolls--phone .rdone__row .rdev__time {
  grid-area: time;
}
</style>
